import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import useAudioCapture from '../../ai-interview/hooks/useAudioCapture';
import useCamera from '../../meeting/hooks/useCamera';
import useScreenShare from '../../meeting/hooks/useScreenShare';
import { startChunkRecording, startScreenRecording, createAiVoiceBus } from '../hooks/recordingManager';
import { recordingStopBeacon, fetchAiTts } from '../../ai-interview/utils/aiInterviewApi';

/**
 * Owns the candidate's media (camera, mic, screen) AND the proctoring recordings for the WHOLE
 * experience, so both survive onboarding → interview without re-prompting or restarting. This
 * matters most for screen sharing: getDisplayMedia prompts on every call, so the share acquired in
 * onboarding must persist into the interview — and the screen recording started the instant it's
 * shared keeps rolling straight through.
 *
 * Recording (driven by the admin `record` config):
 *   • screen → entire shared screen + system audio + mic audio muxed into ONE file (no webcam in it).
 *   • camera / audio → optional separate files, OFF by default (the audio is already in the screen file).
 * Recorders live here (not in the interview page) precisely so a page mount/unmount — including
 * StrictMode's dev double-mount — can't tear them down. On a real page refresh/close they're
 * finalized via sendBeacon so the recording stays viewable up to that point.
 *
 * The mic runs through one AudioCapture pipeline; `setFrameSink(fn)` routes its 16-bit PCM frames
 * to whichever step needs them (voice verification, then interview turns).
 */
const MediaContext = createContext(null);

export const MediaProvider = ({ children, ids, record }) => {
  const camera = useCamera();
  const screen = useScreenShare();
  const capture = useAudioCapture();

  const frameSinkRef = useRef(null);
  const micStartedRef = useRef(false);
  const recordersRef = useRef({});        // kind -> { stop, recordingId, kind }
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const micStreamRef = useRef(null);      // latest live mic stream, for the screen-audio mixer
  micStreamRef.current = capture.recordingStream;
  const rec = record || {};

  // AI-voice bus: carries Wiza's server-TTS audio (ElevenLabs/Piper) to BOTH the live speakers and the
  // recording (one fetch/decode). Created before any recorder starts so its (persistent,
  // silent-until-spoken) track is mixed into the screen + audio files from the very start.
  const aiBusRef = useRef(null);
  const ensureAiBus = useCallback(() => {
    if (!aiBusRef.current) aiBusRef.current = createAiVoiceBus();
    return aiBusRef.current;
  }, []);
  const getAiStream = useCallback(() => (aiBusRef.current ? aiBusRef.current.stream : null), []);

  // Start the mic once; frames are dispatched to the current sink.
  const startMic = useCallback(async () => {
    if (micStartedRef.current && capture.getStream()?.getAudioTracks()[0]?.readyState !== 'live') capture.stop();
    await capture.start({ onFrame: (buf, signal) => { if (frameSinkRef.current) frameSinkRef.current(buf, signal); } });
    micStartedRef.current = true;
  }, [capture]);

  const restartMic = useCallback(async () => {
    capture.stop();
    micStartedRef.current = false;
    await startMic();
  }, [capture, startMic]);

  const setFrameSink = useCallback((fn) => { frameSinkRef.current = fn; }, []);

  // ── recording lifecycle: start when a stream appears, finalize when it goes away ──
  const beginRec = useCallback((kind, stream) => {
    if (!stream || recordersRef.current[kind]) return;
    ensureAiBus();   // bus must exist NOW so its track is mixed into this recording from the start
    const handle = kind === 'screen'
      ? startScreenRecording({ screenStream: stream, getMicStream: () => micStreamRef.current, getAiStream, ids: idsRef.current })
      : startChunkRecording({ stream, kind, getAiStream, ids: idsRef.current });
    if (handle && handle.recordingId) recordersRef.current[kind] = handle;   // ignore no-op handles
  }, [ensureAiBus, getAiStream]);

  // Speak an AI line with the SERVER voice (ElevenLabs/Piper) — LIVE through the speakers AND into the
  // recording from a single fetch. Resolves when playback finishes (so the caller can await it for
  // turn-taking). Returns true if it actually played; false if TTS is unavailable, so the caller can
  // fall back to the browser's SpeechSynthesis.
  const speakAloud = useCallback(async (text, { onStart } = {}) => {
    if (!text) return false;
    const bus = ensureAiBus();
    if (!bus) return false;
    const audio = await fetchAiTts(text);
    if (!audio) return false;
    await bus.play(audio, { toSpeakers: true, onStart });
    return true;
  }, [ensureAiBus]);

  // Stop any in-flight AI line (used on teardown / end-interview).
  const stopAiVoice = useCallback(() => { if (aiBusRef.current) aiBusRef.current.stop(); }, []);

  // Legacy: speak an AI line INTO the recording only (kept for compatibility). speakAloud supersedes
  // it — that path now also drives the live voice.
  const speakToRecording = useCallback(async (text) => {
    if (!text || !Object.values(recordersRef.current).some(Boolean)) return;
    const bus = ensureAiBus();
    if (!bus) return;
    const audio = await fetchAiTts(text);
    if (audio) bus.play(audio);
  }, [ensureAiBus]);

  const endRec = useCallback((kind) => {
    const handle = recordersRef.current[kind];
    if (handle) { recordersRef.current[kind] = null; handle.stop(); }        // finalizes (flush + /stop + remux)
  }, []);

  // Screen: record the entire shared screen (+ system & mic audio) the instant it's shared.
  // Depend on the record flag too, so a config that resolves AFTER the stream still starts the recorder.
  useEffect(() => {
    if (rec.screen !== false && screen.stream) beginRec('screen', screen.stream);
    else if (!screen.stream) endRec('screen');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.stream, rec.screen]);

  // Camera as a separate file — OFF by default (the screen recording is the proctoring video).
  useEffect(() => {
    if (rec.camera === true && camera.stream) beginRec('camera', camera.stream);
    else if (!camera.stream) endRec('camera');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.stream, rec.camera]);

  // Mic-only file — OFF by default (mic audio is already muxed into the screen recording).
  const recordedMicStreamRef = useRef(null);
  useEffect(() => {
    if (rec.audio === true && capture.recordingStream) {
      if (recordedMicStreamRef.current !== capture.recordingStream) {
        endRec('audio');
        recordedMicStreamRef.current = capture.recordingStream;
        beginRec('audio', capture.recordingStream);
      }
    } else {
      endRec('audio');
      recordedMicStreamRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture.recordingStream, rec.audio]);

  const stopRecorders = useCallback(() => {
    Object.keys(recordersRef.current).forEach((k) => endRec(k));
  }, [endRec]);

  const stopAll = useCallback(() => {
    // Finalize recordings first (flush + /stop + remux) — but GUARD it: a throwing recorder must never
    // skip the device teardown below and leave the camera/mic light on.
    try { stopRecorders(); } catch { /* ignore — never let a recorder error keep devices open */ }
    try { capture.stop(); } catch { /* ignore */ }
    try { camera.stop(); } catch { /* ignore */ }
    try { screen.stop(); } catch { /* ignore */ }
    if (aiBusRef.current) { try { aiBusRef.current.close(); } catch { /* ignore */ } aiBusRef.current = null; }
    micStartedRef.current = false;
    frameSinkRef.current = null;
  }, [capture, camera, screen, stopRecorders]);

  // Page refresh / tab close: fetch() gets cancelled on unload, so finalize via sendBeacon. The
  // backend remuxes whatever's on disk → the recording is still viewable up to the cut.
  useEffect(() => {
    const onHide = () => {
      const id = idsRef.current || {};
      Object.values(recordersRef.current).forEach((h) => {
        if (h && h.recordingId) recordingStopBeacon(id.companyId, id.sessionId, id.candidateId, h.kind, h.recordingId);
      });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Release everything (streams + recordings) only when the whole experience unmounts. At initial
  // mount nothing is live yet, so StrictMode's throwaway unmount is a harmless no-op.
  const stopAllRef = useRef(stopAll);
  stopAllRef.current = stopAll;
  useEffect(() => () => stopAllRef.current(), []);

  const value = {
    camera, screen, capture,
    startMic, restartMic, setFrameSink, stopAll, speakToRecording, speakAloud, stopAiVoice,
    micStarted: () => micStartedRef.current,
  };
  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
};

export const useMedia = () => {
  const ctx = useContext(MediaContext);
  if (!ctx) throw new Error('useMedia must be used within <MediaProvider>');
  return ctx;
};

export default MediaContext;
